import type { AgendaItem } from '@/types';

interface ExpandedItem {
  id: string;
  text: string;
  expandedPhrases: string[];
  expandedKeywords: string[];
}

interface ExpandAgendaResponse {
  items: ExpandedItem[];
  cached: boolean;
}

/**
 * Get the API base URL based on environment
 */
function getApiBaseUrl(): string {
  return `${window.location.protocol}//${window.location.host}`;
}

/**
 * Calls the backend to expand agenda items with LLM-generated phrases and keywords.
 * This is called once at session start to enhance matching accuracy.
 */
export async function expandAgendaItems(items: AgendaItem[]): Promise<Map<string, ExpandedItem>> {
  const startTime = Date.now();
  console.log('🤖 Requesting agenda expansion from server...');

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/expand-agenda`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: items.map(item => ({
          id: item.id,
          text: item.text,
          isGroupHeader: item.isGroupHeader,
        })),
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: ExpandAgendaResponse = await response.json();
    const elapsed = Date.now() - startTime;

    console.log(`✅ Agenda expansion complete in ${elapsed}ms (cached: ${data.cached})`);

    // Convert to Map for quick lookups
    const expansionMap = new Map<string, ExpandedItem>();
    for (const item of data.items) {
      expansionMap.set(item.id, item);
      if (item.expandedPhrases.length > 0) {
        console.log(`  📝 "${item.text.substring(0, 30)}..." → ${item.expandedPhrases.length} phrases, ${item.expandedKeywords.length} keywords`);
      }
    }

    return expansionMap;
  } catch (error) {
    console.error('❌ Agenda expansion failed:', error);
    // Return empty map - matching will work without expansion, just less accurately
    return new Map();
  }
}

/**
 * Merge LLM-generated keywords into existing agenda items
 */
export function mergeExpansionIntoAgenda(
  items: AgendaItem[],
  expansions: Map<string, ExpandedItem>
): AgendaItem[] {
  return items.map(item => {
    const expansion = expansions.get(item.id);
    if (!expansion || expansion.expandedKeywords.length === 0) {
      return item;
    }

    // Merge expanded keywords with existing keywords (deduplicated)
    const mergedKeywords = [...new Set([
      ...item.keywords,
      ...expansion.expandedKeywords.map(k => k.toLowerCase()),
    ])];

    return {
      ...item,
      keywords: mergedKeywords,
      // Store expanded phrases for potential use in matching
      _expandedPhrases: expansion.expandedPhrases,
    } as AgendaItem & { _expandedPhrases?: string[] };
  });
}
